// Importing necessary libraries and components
import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, TextField, Checkbox } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import styles from "assets/jss/material-dashboard-react/components/businessForm";
import { MuiPickersUtilsProvider, DatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format, parseISO } from "date-fns";
import { createTheme } from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";
import {
  getTransactionListData,
  setFormData,
  clearFormData,
} from "../../Redux/Actions/merchantInformation";
import { profileCodePermissionCheck } from "../../Utils/commonUtils";

// Custom theme for Material-UI components
export const customTheme = createTheme({
  palette: {
    primary: {
      main: "#003c6e", // Primary color
      light: "#003c6e",
      dark: "#003c6e",
    },
    secondary: {
      main: "#003c6e", // Secondary color
    },
  },
  overrides: {
    MuiPickersBasePicker: {
      container: {
        width: "300px",
        height: "370px",
      },
      pickerView: {
        width: "280px",
        height: "280px",
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        fontSize: "0.9rem",
      },
      dayLabel: {
        fontSize: "0.7rem",
      },
    },
    MuiPickersDay: {
      day: {
        fontSize: "0.8rem",
      },
    },
    MuiTypography: {
      h4: {
        fontSize: "1rem",
      },
    },
  },
});

// Custom styles for the component
const useStyles = makeStyles(styles);
const searchButtonStyles = makeStyles(theme => ({
  searchButton: {
    backgroundColor: theme.palette.primary.main, // Button background color
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      boxShadow: "0 14px 26px -12px rgb(153 153 153 / 42%), 0 4px 23px 0px rgb(0 0 0 / 12%), 0 8px 10px -5px rgb(153 153 153 / 20%)",
    },
  },
}));

// Main component for Search Transaction Dialog
function SearchTxn(props) {
  const classes = useStyles();
  const [showAmountRange, setShowAmountRange] = React.useState(false); // Toggle for amount range
  const [showDateRange, setShowDateRange] = React.useState(false); // Toggle for date range
  const searchButtonClasses = searchButtonStyles();
  const profileId = localStorage.getItem("profileId");
  const menu = JSON.parse(localStorage.getItem("menu"));

  // State variables for form inputs
  const [sender1, setSender1] = React.useState(""); // Sender name
  const [txn1, setTxn1] = React.useState(""); // Transaction ID
  const [amount1, setAmount1] = React.useState(""); // Start amount
  const [amount2, setAmount2] = React.useState(""); // End amount
  const [date1, setDate1] = React.useState(""); // Start date
  const [date2, setDate2] = React.useState(""); // End date
  const [status1, setStatus1] = React.useState(""); // Status

  // Effect to initialize amount and date range toggles from localStorage
  React.useEffect(() => {
    const savedIsAmountRangeChecked = localStorage.getItem(
      "isAmountRangeChecked"
    );
    setShowAmountRange(savedIsAmountRangeChecked === "true");

    const savedIsDateRangeChecked = localStorage.getItem("isDateRangeChecked");
    setShowDateRange(savedIsDateRangeChecked === "true");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to clear form data
  const clearFormData = () => {
    const initialFormData = {
      searchSenderName: "",
      searchTxnID: "",
      searchStatus: null,
      searchStatusValue: "",
      searchStartAmount: "",
      searchEndAmount: "",
      searchStartDate: "",
      searchEndDate: "",
      dateRange: false,
      amountRange: false,
    };

    setSender1("");
    setTxn1("");
    setAmount1("");
    setAmount2("");
    setDate1("");
    setDate2("");
    setStatus1("");
    setShowAmountRange(false);
    setShowDateRange(false);

    localStorage.removeItem("isAmountRangeChecked");
    localStorage.removeItem("isDateRangeChecked");
    localStorage.removeItem("searchData");

    props.setFormData(initialFormData);
  };

  // Function to handle amount range toggle
  const handleAmountCheckboxChange = event => {
    setShowAmountRange(event.target.checked);
    if (event.target.checked) {
      setAmount1("");
      setShowAmountRange(true);
    } else {
      setShowAmountRange(false);
      setAmount1("");
      setAmount2("");
    }
  };

  // Function to handle date range toggle
  const handleDateCheckboxChange = event => {
    setShowDateRange(event.target.checked);
    if (event.target.checked) {
      setDate1("");
      setShowDateRange(true);
    } else {
      setDate1("");
      setDate2("");
      setShowDateRange(false);
    }
  };

  const handleFromDateChange = date => {
    let formattedDate = null;
    if (date instanceof Date) {
      formattedDate = format(date, "yyyy-MM-dd");
    } else if (typeof date === "string") {
      const parts = date.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    setDate1(formattedDate);
  };

  const handleToDateChange = date => {
    let formattedDate = null;
    if (date instanceof Date) {
      formattedDate = format(date, "yyyy-MM-dd");
    } else if (typeof date === "string") {
      const parts = date.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }
    setDate2(formattedDate);
  };

  // Function to handle dialog close
  const handleClose = () => {
    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.SA) {
      const payload = {
        Profile_id: profileId,
        recordPerPage: 5,
        pageNo: 1,
      };
      props.getTransactionListData(payload);
    } else {
      const payload = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        recordPerPage: 5,
        pageNo: 1,
      };
      props.getTransactionListData(payload);
    }
    props.clearFormData();
    clearFormData();
    props.clearChips();
  };

  const onChange = e => {
    const { name, value } = e.target;

    if (name === "searchStatus") {
      setStatus1(value);
    }
  };
  const onFormSubmit = event => {
    event.preventDefault();
    const submitFormData = {
      searchSenderName: sender1.trim(),
      searchTxnID: txn1.trim(),
      searchStartAmount: amount1.trim(),
      searchEndAmount: amount2.trim(),
      searchStartDate: date1,
      searchEndDate: date2,
      searchStatus: status1.trim(),
      amountRange: showAmountRange,
      dateRange: showDateRange,
    };

    localStorage.setItem("searchData", JSON.stringify(submitFormData));
    if (showAmountRange === true) {
      localStorage.setItem("isAmountRangeChecked", true);
    } else {
      localStorage.removeItem("isAmountRangeChecked");
    }

    if (showDateRange === true) {
      localStorage.setItem("isDateRangeChecked", true);
    } else {
      localStorage.removeItem("isDateRangeChecked");
    }

    props.setFormData(submitFormData);

    const profileCode = menu?.Role_Permission_Data?.Profile_Code;
    if (profileCode === profileCodePermissionCheck.profileCode.SA) {
      const payload = {
        Profile_id: profileId,
        recordPerPage: props?.formData?.rowsPerPage,
        pageNo: 1,
        searchSenderName: sender1.trim(),
        searchTxnID: txn1.trim(),
        searchStatus: status1.trim(),
        ...(showAmountRange
          ? {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }
          : {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }),
        ...(showDateRange
          ? {
            searchStartDate: date1,
            searchEndDate: date2,
          }
          : {
            searchStartDate: date1,
            searchEndDate: date2,
          }),

        dateRange: showDateRange,
        amountRange: showAmountRange,
      };
      props.getTransactionListData(payload);
      props.seacrhChipData(payload);
      props.close();
    } else {
      const payload = {
        Profile_Code: menu?.Role_Permission_Data?.Profile_Code,
        Profile_id: profileId,
        pageNo: 1,
        recordPerPage: props?.formData?.rowsPerPage,
        sortColumn: props?.formData?.sortColumn,
        sortOrder: props?.formData?.sortOrder,
        searchSenderName: sender1.trim(),
        searchTxnID: txn1.trim(),
        searchStatus: status1.trim(),
        ...(showAmountRange
          ? {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }
          : {
            searchStartAmount: amount1.trim(),
            searchEndAmount: amount2.trim(),
          }),
        ...(showDateRange
          ? {
            searchStartDate: date1,
            searchEndDate: date2,
          }
          : {
            searchStartDate: date1,
            searchEndDate: date2,
          }),

        dateRange: showDateRange,
        amountRange: showAmountRange,
      };
      props.getTransactionListData(payload);
      props.seacrhChipData(payload);
      props.close();
    }
  };

  // Effect to populate form fields from Redux state
  React.useEffect(() => {
    if (props.formData.searchSenderName) {
      setSender1(props.formData.searchSenderName);
    }
    if (props.formData.searchTxnID) {
      setTxn1(props.formData.searchTxnID);
    }
    if (props.formData.searchStartAmount) {
      setAmount1(props.formData.searchStartAmount);
    }
    if (props.formData.searchEndAmount) {
      setAmount2(props.formData.searchEndAmount);
    }
    if (props.formData.searchStartDate) {
      setDate1(props.formData.searchStartDate);
    }
    if (props.formData.searchEndDate) {
      setDate2(props.formData.searchEndDate);
    }
    if (props.formData.searchStatus) {
      setStatus1(props.formData.searchStatus);
    }
  }, [props.formData]);

  const handleInputChange = e => {
    setSender1(e.target.value);
  };

  const handleInputChange1 = e => {
    setTxn1(e.target.value);
  };

  const handleInputChange2 = e => {
    setAmount1(e.target.value);
  };

  const handleInputChange3 = e => {
    setAmount2(e.target.value);
  };

  return (
    <MuiThemeProvider theme={customTheme}>
      <form onSubmit={e => onFormSubmit(e)}>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label="Sender"
              value={sender1}
              onChange={handleInputChange}
              name="searchSenderName"
              id="Sender Name"
              style={{ fontSize: "82%" }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label="Txn ID"
              value={txn1}
              onChange={handleInputChange1}
              name="searchTxnID"
              id="txnID"
              style={{ fontSize: "82%" }}
            />
          </Grid>
        </Grid>

        {/* Amount Range Inputs */}
        <Grid container className={classes.margin}>
          <Grid item xs container alignItems="center">
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLabelColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  focused: "focused",
                  input: classes.resize,
                },
              }}
              label={showAmountRange ? "From" : "Amount ($)"}
              value={amount1}
              onChange={handleInputChange2}
              name="searchStartAmount"
              id="searchStartAmount"
              style={{
                fontSize: "82%",
                width: showAmountRange ? "50%" : "100%",
              }}
            />
            {showAmountRange && (
              <>
                <TextField
                  className={classes.rootDialog}
                  InputLabelProps={{
                    classes: {
                      root: classes.activeInputLabelColor,
                      focused: "focused",
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.activeInputColor,
                      focused: "focused",
                      input: classes.resize,
                    },
                  }}
                  label="To"
                  value={amount2}
                  onChange={handleInputChange3}
                  name="searchEndAmount"
                  id="searchEndAmount"
                  style={{ fontSize: "82%", width: "50%" }}
                />
              </>
            )}
            <Checkbox
              checked={showAmountRange}
              onChange={handleAmountCheckboxChange}
              color={classes.activeInputColor}
              style={{ marginLeft: "auto" }}
            />
            <label style={{ fontSize: "12px", marginBottom: 0 }}>
              Search In Range
            </label>
          </Grid>
        </Grid>

        {/* Date Range Inputs */}
        <Grid container className={classes.margin}>
          <Grid item xs container alignItems="center">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePicker
                autoOk
                variant="inline"
                label={showDateRange ? "From" : "Date"}
                value={date1 ? parseISO(date1) : null}
                onChange={handleFromDateChange}
                format="yyyy/MM/dd"
                maxDate={new Date()}
                style={{
                  width: showDateRange ? "50%" : "100%",
                }}
              />

              {inputProps => (
                <TextField
                  {...inputProps}
                  className={classes.rootDialog}
                  InputLabelProps={{
                    classes: {
                      root: classes.activeInputLabelColor,
                      focused: "focused",
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: classes.activeInputColor,
                      focused: "focused",
                      input: classes.resize,
                    },
                  }}
                  label={showDateRange ? "From" : "Date"}
                  style={{
                    width: showDateRange ? "50%" : "100%",
                    fontSize: "100%",
                  }}
                />
              )}
            </MuiPickersUtilsProvider>
            {showDateRange && (
              <>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <DatePicker
                    autoOk
                    variant="inline"
                    label="To"
                    value={date2 ? parseISO(date2) : null}
                    onChange={handleToDateChange}
                    format="yyyy/MM/dd"
                    maxDate={new Date()}
                    style={{
                      width: "50%",
                    }}
                  />
                  {inputProps => (
                    <TextField
                      {...inputProps}
                      className={classes.rootDialog}
                      InputLabelProps={{
                        classes: {
                          root: classes.activeInputLabelColor,
                          focused: "focused",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.activeInputColor,
                          focused: "focused",
                          input: classes.resize,
                        },
                      }}
                      label="To"
                      style={{ width: "50%", fontSize: "82%" }}
                    />
                  )}
                </MuiPickersUtilsProvider>
              </>
            )}
            <Checkbox
              checked={showDateRange}
              onChange={handleDateCheckboxChange}
              color="primary"
              style={{ marginLeft: "auto" }}
            />
            <label style={{ fontSize: "12px", marginBottom: 0 }}>
              Search In Range
            </label>
          </Grid>
        </Grid>

        <Grid
          container
          className={classes.margin}
          style={{ marginBottom: "5%" }}
        >
          <Grid item xs>
            <TextField
              className={classes.rootDialog}
              InputLabelProps={{
                classes: {
                  root: classes.activeInputLableColor,
                  focused: "focused",
                },
              }}
              InputProps={{
                classes: {
                  root: classes.activeInputColor,
                  input: classes.resize,
                  focused: "focused",
                },
              }}
              placeholder="Select Status"
              label="Status"
              value={status1}
              onChange={onChange}
              name="searchStatus"
              id="Current_Status"
              select
              style={{ fontSize: "82%" }}
            >
              <MenuItem className={classes.dialogBoxDropDown} value="Reufnded">
                Refunded
              </MenuItem>
              <MenuItem className={classes.dialogBoxDropDown} value="Completed">
                Completed
              </MenuItem>
              <MenuItem
                className={classes.dialogBoxDropDown}
                value="InProgress"
              >
                InProgress
              </MenuItem>
              <MenuItem className={classes.dialogBoxDropDown} value="Canceled">
                Canceled
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Grid
          container
          className={classes.margin}
          style={{ justifyContent: "end" }}
        >
          <Button
            variant="contained"
            className={classes.clearButton}
            disabled={
              sender1 === ""
              && txn1 === ""
              && status1 === ""
              && amount1 === ""
              && amount2 === ""
              && date1 === ""
              && date2 === ""
            }
            onClick={() => handleClose()}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={`${classes.buttonStyle} ${searchButtonClasses.searchButton}`}
            disabled={
              sender1 === ""
              && txn1 === ""
              && status1 === ""
              && amount1 === ""
              && amount2 === ""
              && date1 === ""
              && date2 === ""
            }
            type="submit"
          >
            Search
          </Button>
        </Grid>
      </form>
    </MuiThemeProvider>
  );
}

// Prop types for the component
SearchTxn.propTypes = {
  close: PropTypes.func,
  tableData: PropTypes.object,
  seacrhChipData: PropTypes.func,
  getTransactionListData: PropTypes.func,
  deleteChp: PropTypes.any,
};

// Mapping Redux state to props
const mapStateToProps = app => ({
  formData: app.merchantInformation,
  tableData: app.provideRegistration,
  userDetails: app.app,
  merchantData: app.merchantInformation,
  transactionData: app.merchantInformation,
});

// Connecting the component to Redux
export default connect(mapStateToProps, {
  setFormData,
  clearFormData,
  getTransactionListData,
})(SearchTxn);
